import axios from "axios";
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

export const authContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(authContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthContextProvider');
  }
  return context;
};

export const AuthContextProvider = (props) => {

  axios.defaults.withCredentials = true;
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";

  const [isLoggedin, setIsLoggedin] = useState(false);
  const [userData, setUserData] = useState(false);
  const [loading, setLoading] = useState(true);

  const normalizeUser = (raw) => {
    if (!raw) return null;
    const role = raw.role === 'creater' ? 'teacher' : raw.role;
    return { ...raw, role };
  };

  const getUserData = useCallback(async () => {
    try {
      const { data } = await axios.get(backendUrl + "/api/auth/user-details");
      if (data.success) {
        const raw = data.userData || data.userDeatails; // backend sends userDeatails
        const normalized = normalizeUser(raw);
        setUserData(normalized);
        return normalized;
      } else {
        toast.error(data.message);
        return null;
      }
    } catch (error) {
      toast.error(error.message);
      return null;
    }
  }, [backendUrl]);

  const getAuthState = useCallback(async () => {
    try {
      const { data } = await axios.get(backendUrl + "/api/auth/user-details");
      if (data.success) {
        setIsLoggedin(true);
        const raw = data.userData || data.userDeatails;
        const normalized = normalizeUser(raw);
        setUserData(normalized);
      }
    } catch (error) {
      // unauthenticated is fine on first load
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);
  
  useEffect(() => {
    getAuthState();
  }, [getAuthState]);

  const login = async (email, password) => {
    try {
      console.log("Attempting login to:", backendUrl + "/api/auth/login");
      const { data } = await axios.post(backendUrl + "/api/auth/login", {
        email,
        password
      });
      if (data.success) {
        setIsLoggedin(true);
        const normalized = await getUserData();
        toast.success("Login successful!");
        return { success: true, userData: normalized };
      } else {
        toast.error(data.message || "Login failed");
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response?.status === 404) {
        toast.error("Login endpoint not found. Check if backend is running on port 3000");
      } else {
        toast.error(error.response?.data?.message || error.message || "Login failed");
      }
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, email, password, role) => {
    try {
      console.log("Attempting register to:", backendUrl + "/api/auth/register");
      const backendRole = role === 'teacher' ? 'creater' : role;
      const { data } = await axios.post(backendUrl + "/api/auth/register", {
        name,
        email,
        password,
        role: backendRole
      });
      if (data.success) {
        setIsLoggedin(true);
        const normalized = await getUserData();
        toast.success("Registration successful!");
        return { success: true, userData: normalized };
      } else {
        toast.error(data.message || "Registration failed");
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error("Register error:", error);
      if (error.response?.status === 404) {
        toast.error("Register endpoint not found. Check if backend is running on port 3000");
      } else {
        toast.error(error.response?.data?.message || error.message || "Registration failed");
      }
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(backendUrl + "/api/auth/logout");
    } catch (error) {
      console.log("Logout error:", error);
    } finally {
      setIsLoggedin(false);
      setUserData(null);
      toast.success("Logged out successfully!");
    }
  };

  const isTeacher = () => userData?.role === 'teacher';
  const isStudent = () => userData?.role === 'student';

  const value = {
    backendUrl,
    isLoggedin,
    setIsLoggedin,
    userData,
    setUserData,
    getUserData,
    login,
    register,
    logout,
    isTeacher,
    isStudent,
    loading
  };

  return (
    <authContext.Provider value={value}>
      {props.children}
    </authContext.Provider>
  );
};
